import React, { useEffect, useState, useCallback } from 'react';
import { X, Plus, RefreshCw } from 'lucide-react';
import { api } from '../api';
import Tip from '../components/Tip';

interface Holding {
  symbol: string;
  quantity: number;
}

interface PriceInfo {
  price: number;
  change24h: number;
}

const Portfolio: React.FC = () => {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [prices, setPrices] = useState<Record<string, PriceInfo>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editQty, setEditQty] = useState('');
  const [availableCoins, setAvailableCoins] = useState<string[]>([]);

  // Load available coins for suggestions
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/coins/available`)
      .then(r => r.json())
      .then((coins: any[]) => setAvailableCoins(coins.map((c: any) => c.symbol || c.pair?.replace('-USD', '') || '').filter(Boolean)))
      .catch(() => {});
  }, []);

  // Load holdings on mount
  useEffect(() => {
    api.getPortfolio()
      .then((data: any[]) => setHoldings(data.map(d => ({ symbol: d.symbol, quantity: Number(d.quantity) }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Fetch prices when holdings change + 60s interval
  const fetchPrices = useCallback(() => {
    if (holdings.length === 0) { setPrices({}); return; }
    const symbols = holdings.map(h => h.symbol);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${API_URL}/api/prices?symbols=${symbols.join(',')}`)
      .then(r => r.json())
      .then((data: any[]) => {
        const map: Record<string, PriceInfo> = {};
        if (Array.isArray(data)) {
          data.forEach((c: any) => {
            if (c.symbol) map[c.symbol] = { price: c.price || 0, change24h: c.change24h || 0 };
          });
        }
        setPrices(map);
      })
      .catch(console.error);
  }, [holdings]);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  // Computed values
  const rows = holdings.map(h => {
    const p = prices[h.symbol];
    const price = p?.price || 0;
    const value = h.quantity * price;
    const change = p?.change24h || 0;
    return { ...h, price, value, change };
  });

  const totalValue = rows.reduce((s, r) => s + r.value, 0);
  const totalChange = rows.reduce((s, r) => {
    if (!r.price || !r.change) return s;
    const prev = r.value / (1 + r.change / 100);
    return s + (r.value - prev);
  }, 0);
  const totalChangePct = totalValue > 0 && totalChange !== 0
    ? (totalChange / (totalValue - totalChange)) * 100
    : 0;

  const largest = rows.length > 0
    ? rows.reduce((a, b) => b.value > a.value ? b : a)
    : null;

  // Handlers
  const handleAdd = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym || !newQuantity || Number(newQuantity) <= 0) return;
    if (holdings.some(h => h.symbol === sym)) return; // already exists
    setSaving(true);
    try {
      await api.updatePortfolio([...holdings, { symbol: sym, quantity: Number(newQuantity) }]);
      setHoldings(prev => [...prev, { symbol: sym, quantity: Number(newQuantity) }]);
      setNewSymbol('');
      setNewQuantity('');
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleRemove = async (symbol: string) => {
    setSaving(true);
    try {
      await api.deletePortfolioHolding(symbol);
      setHoldings(prev => prev.filter(h => h.symbol !== symbol));
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleEditSave = async (idx: number) => {
    const qty = Number(editQty);
    if (!qty || qty <= 0) { setEditingIdx(null); return; }
    const updated = holdings.map((h, i) => i === idx ? { ...h, quantity: qty } : h);
    setSaving(true);
    try {
      await api.updatePortfolio(updated);
      setHoldings(updated);
    } catch (err) { console.error(err); }
    setSaving(false);
    setEditingIdx(null);
  };

  const fmt = (n: number) => n >= 1 ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n.toPrecision(4);
  const fmtUsd = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#71717a' }}>Loading portfolio...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Portfolio</div>
        <div className="header-actions">
          {saving && <span style={{ fontSize: 12, color: '#71717a' }}>Saving...</span>}
          <button className="btn" onClick={fetchPrices}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-row stats-4">
        <div className="stat-card">
          <div className="stat-label">Total Value <Tip text="Sum of all holdings at current market prices" /></div>
          <div className="stat-value">{fmtUsd(totalValue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Assets</div>
          <div className="stat-value">{holdings.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Largest Position <Tip text="Your biggest holding by dollar value and its percentage of total portfolio" /></div>
          <div className="stat-value">{largest ? largest.symbol : '—'}</div>
          <div className="stat-sub gray">{largest && totalValue > 0 ? `${(largest.value / totalValue * 100).toFixed(1)}% of portfolio` : ''}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">24h Change <Tip text="Total portfolio value change in the last 24 hours based on price movement" /></div>
          <div className={`stat-value ${totalChange >= 0 ? 'green' : 'red'}`}>
            {totalChange >= 0 ? '+' : ''}{fmtUsd(totalChange)}
          </div>
          <div className={`stat-sub ${totalChangePct >= 0 ? 'green' : 'red'}`}>
            {totalChangePct >= 0 ? '+' : ''}{totalChangePct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Holdings */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Holdings</div>
          <span className="badge badge-gray">{holdings.length} assets</span>
        </div>

        {holdings.length === 0 ? (
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon" style={{ opacity: 0.4, fontSize: 36 }}>&#x1F4B0;</div>
              <div className="empty-state-text">No holdings yet</div>
              <div className="empty-state-sub">Add your crypto holdings below to track portfolio value</div>
            </div>
          </div>
        ) : (
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th style={{ textAlign: 'right' }}>Quantity</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ textAlign: 'right' }}>Value</th>
                <th style={{ textAlign: 'right' }}>Allocation</th>
                <th style={{ textAlign: 'right' }}>24h</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const alloc = totalValue > 0 ? (row.value / totalValue * 100) : 0;
                return (
                  <tr key={row.symbol}>
                    <td>
                      <span style={{ fontWeight: 700 }}>{row.symbol}</span>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {editingIdx === idx ? (
                        <input
                          type="number"
                          className="form-input"
                          value={editQty}
                          onChange={e => setEditQty(e.target.value)}
                          onBlur={() => handleEditSave(idx)}
                          onKeyDown={e => e.key === 'Enter' && handleEditSave(idx)}
                          autoFocus
                          style={{ width: 100, textAlign: 'right', padding: '4px 8px', fontSize: 13 }}
                        />
                      ) : (
                        <span
                          onClick={() => { setEditingIdx(idx); setEditQty(String(row.quantity)); }}
                          style={{ cursor: 'pointer', borderBottom: '1px dashed #3f3f46' }}
                          title="Click to edit"
                        >
                          {fmt(row.quantity)}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {row.price > 0 ? fmtUsd(row.price) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {row.value > 0 ? fmtUsd(row.value) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{alloc.toFixed(1)}%</span>
                        <div style={{ width: 60, height: 4, background: '#1e2028', borderRadius: 2 }}>
                          <div style={{ width: `${Math.min(alloc, 100)}%`, height: '100%', background: '#3b82f6', borderRadius: 2 }} />
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <span className={row.change >= 0 ? 'green' : 'red'}>
                        {row.change >= 0 ? '+' : ''}{row.change.toFixed(2)}%
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleRemove(row.symbol)}
                        style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: 4 }}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {/* Add row */}
        <div className="card-body" style={{ borderTop: '1px solid #2a2d35' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Symbol (e.g. BTC)"
                value={newSymbol}
                onChange={e => setNewSymbol(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                list="coin-suggestions"
                style={{ width: '100%' }}
              />
              <datalist id="coin-suggestions">
                {availableCoins
                  .filter(c => !holdings.some(h => h.symbol === c))
                  .map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <input
              type="number"
              className="form-input"
              placeholder="Quantity"
              value={newQuantity}
              onChange={e => setNewQuantity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              style={{ width: 120 }}
              min="0"
              step="any"
            />
            <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !newSymbol || !newQuantity}>
              <Plus size={14} /> Add
            </button>
          </div>
          {holdings.some(h => h.symbol === newSymbol.trim().toUpperCase()) && newSymbol && (
            <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>
              {newSymbol.toUpperCase()} is already in your portfolio. Click the quantity to edit it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
